import { useEffect, useState } from "react";
import { api, type AuditEntryView } from "../api";

// SIMO-UI-MOBILE-RESPONSIVE-AND-ESCAPE-PASS-1 (F-32):
// 7-spaltige Tabelle bricht auf Mobile (< md). Card-Fallback rendert
// dieselben Daten als vertikale Liste, die Tabelle bleibt fuer Desktop
// erhalten (groesste Datenmenge laesst sich dort am besten scannen).
function badgeClasses(result: AuditEntryView["result"]): string {
  switch (result) {
    case "ok":
      return "bg-emerald-500/20 text-emerald-300";
    case "blocked":
    case "error":
      return "bg-rose-500/20 text-rose-300";
    case "draft":
      return "bg-amber-500/20 text-amber-300";
    case "pending":
      return "bg-blue-500/20 text-blue-300";
    default:
      return "bg-rose-500/20 text-rose-300";
  }
}

export function AuditView(): JSX.Element {
  const [items, setItems] = useState<AuditEntryView[]>([]);
  const [verify, setVerify] = useState<string>("");

  const load = (): void => {
    void api.listAudit(200).then((r) => setItems(r.items));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const doVerify = async (): Promise<void> => {
    const r = await api.verifyAudit();
    setVerify(r.ok ? "Hash-Kette intakt ✓" : `Bruch ab ID ${r.brokenAt}`);
  };

  return (
    <div className="h-full flex flex-col p-4 sm:p-6 gap-4 overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h1 className="text-2xl font-semibold">Audit-Log</h1>
        <div className="flex items-center gap-3 flex-wrap">
          {verify && <span className="text-xs text-jarvis-muted">{verify}</span>}
          <button className="btn" onClick={() => void doVerify()}>
            Hash-Kette prüfen
          </button>
          <button className="btn" onClick={load}>
            Neu laden
          </button>
        </div>
      </div>

      {/* Desktop ≥ md: vollstaendige Tabelle */}
      <div className="flex-1 overflow-auto card hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-jarvis-bg/50 sticky top-0">
            <tr className="text-left text-xs uppercase tracking-wider text-jarvis-muted">
              <th className="px-3 py-2">Zeit</th>
              <th className="px-3 py-2">Akteur</th>
              <th className="px-3 py-2">Connector</th>
              <th className="px-3 py-2">Aktion</th>
              <th className="px-3 py-2">Modus</th>
              <th className="px-3 py-2">Ergebnis</th>
              <th className="px-3 py-2">Zusammenfassung</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr
                key={e.id}
                className="border-t border-jarvis-border hover:bg-jarvis-bg/40"
              >
                <td className="px-3 py-2 text-xs text-jarvis-muted whitespace-nowrap">
                  {new Date(e.timestamp).toLocaleString("de-DE")}
                </td>
                <td className="px-3 py-2 text-xs">{e.actor}</td>
                <td className="px-3 py-2 text-xs">{e.connector ?? ""}</td>
                <td className="px-3 py-2 text-xs font-mono">{e.actionId ?? ""}</td>
                <td className="px-3 py-2 text-xs">{e.mode ?? ""}</td>
                <td className="px-3 py-2">
                  <span className={`badge ${badgeClasses(e.result)}`}>{e.result}</span>
                </td>
                <td className="px-3 py-2">{e.summary}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-jarvis-muted text-sm">
                  Noch keine Einträge.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile < md: Card-Fallback */}
      <div className="flex-1 overflow-auto md:hidden space-y-2">
        {items.length === 0 ? (
          <div className="card p-6 text-center text-jarvis-muted text-sm">
            Noch keine Einträge.
          </div>
        ) : (
          items.map((e) => (
            <div key={e.id} className="card p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-jarvis-muted">
                  {new Date(e.timestamp).toLocaleString("de-DE")}
                </div>
                <span className={`badge ${badgeClasses(e.result)}`}>{e.result}</span>
              </div>
              <div className="text-sm break-words">{e.summary}</div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-jarvis-muted">
                <span>
                  <span className="uppercase tracking-wider">Akteur:</span> {e.actor}
                </span>
                {e.connector && (
                  <span>
                    <span className="uppercase tracking-wider">Connector:</span>{" "}
                    {e.connector}
                  </span>
                )}
                {e.actionId && (
                  <span className="font-mono">
                    <span className="uppercase tracking-wider not-italic font-sans">
                      Aktion:
                    </span>{" "}
                    {e.actionId}
                  </span>
                )}
                {e.mode && (
                  <span>
                    <span className="uppercase tracking-wider">Modus:</span> {e.mode}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
